import React, { useState } from 'react';
import { GoGoal } from "react-icons/go";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "../global/api";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Select } from 'antd';
import { Units } from "../../utils/Constants";

function getCurrentYearAndQuarter() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-11 (January is 0)
  
  // Determine quarter (Q1: 0-2, Q2: 3-5, Q3: 6-8, Q4: 9-11)
  const quarter = Math.floor(month / 3) + 1;
  
  return `${year}_Q${quarter}`;
}

const GoalsFromFileResponseMessage = ({ message, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loadingGoalsOnly, setLoadingGoalsOnly] = useState(false);
  const [loadingGoalsAndObjectives, setLoadingGoalsAndObjectives] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [editableGoals, setEditableGoals] = useState([]);
  const userold = useSelector((state) => state.user);
  const currentUser = userold?.currentUser;
  const Sessions = useSelector((state) => state.user?.sessionList);

  // Initialize editable goals when component mounts
  React.useEffect(() => {
    if (message?.response?.goals) {
      const initializedGoals = message.response.goals.map(goal => ({
        ...goal,
        isEditing: false,
        children: Array.isArray(goal.children) ? goal.children.map(objective => ({
          ...objective,
          isEditing: false,
          children: Array.isArray(objective.children) ? objective.children.map(kr => ({
            ...kr,
            isEditing: false
          })) : []
        })) : []
      }));
      setEditableGoals(initializedGoals);
    }
  }, [message]);

  const handleEdit = (type, index, parentIndex = null, grandParentIndex = null) => {
    console.log('handleEdit called with:', { type, index, parentIndex, grandParentIndex });
    setEditableGoals(prevGoals => {
      if (!Array.isArray(prevGoals)) return [];
      
      const newGoals = [...prevGoals];
      console.log('Current goals state:', newGoals);
      
      if (type === 'goal') {
        if (newGoals[index]) {
          newGoals[index] = { ...newGoals[index], isEditing: !newGoals[index].isEditing };
        }
      } else if (type === 'objective') {
        if (newGoals[grandParentIndex] && !newGoals[grandParentIndex].children) {
          newGoals[grandParentIndex].children = [];
        }
        if (newGoals[grandParentIndex]?.children?.[index]) {
          newGoals[grandParentIndex].children[index] = {
            ...newGoals[grandParentIndex].children[index],
            isEditing: !newGoals[grandParentIndex].children[index].isEditing
          };
        }
      } else if (type === 'keyResult') {
        if (newGoals[grandParentIndex]?.children?.[parentIndex] && !newGoals[grandParentIndex].children[parentIndex].children) {
          newGoals[grandParentIndex].children[parentIndex].children = [];
        }
        if (newGoals[grandParentIndex]?.children?.[parentIndex]?.children?.[index]) {
          newGoals[grandParentIndex].children[parentIndex].children[index] = {
            ...newGoals[grandParentIndex].children[parentIndex].children[index],
            isEditing: !newGoals[grandParentIndex].children[parentIndex].children[index].isEditing
          };
        }
      }
      console.log('Updated goals state:', newGoals);
      return newGoals;
    });
  };

  const handleInputChange = (type, index, field, value, parentIndex = null, grandParentIndex = null) => {
    console.log('handleInputChange called with:', { type, index, field, value, parentIndex, grandParentIndex });
    setEditableGoals(prevGoals => {
      if (!Array.isArray(prevGoals)) return [];
      
      const newGoals = [...prevGoals];
      console.log('Current goals state before change:', newGoals);
      
      if (type === 'goal') {
        if (newGoals[index]) {
          newGoals[index] = { ...newGoals[index], [field]: value };
        }
      } else if (type === 'objective') {
        if (newGoals[grandParentIndex] && !newGoals[grandParentIndex].children) {
          newGoals[grandParentIndex].children = [];
        }
        if (newGoals[grandParentIndex]?.children?.[index]) {
          newGoals[grandParentIndex].children[index] = {
            ...newGoals[grandParentIndex].children[index],
            [field]: value
          };
        }
      } else if (type === 'keyResult') {
        if (newGoals[grandParentIndex]?.children?.[parentIndex] && !newGoals[grandParentIndex].children[parentIndex].children) {
          newGoals[grandParentIndex].children[parentIndex].children = [];
        }
        if (newGoals[grandParentIndex]?.children?.[parentIndex]?.children?.[index]) {
          newGoals[grandParentIndex].children[parentIndex].children[index] = {
            ...newGoals[grandParentIndex].children[parentIndex].children[index],
            [field]: value
          };
        }
      }
      console.log('Updated goals state after change:', newGoals);
      return newGoals;
    });
  };

  const handleClose = () => {
    setIsSaved(false);
    if (onClose) {
      onClose();
    }
  };

  const saveData = async (saveType = 'all') => {
    try {
      // Set appropriate loading state based on save type
      switch (saveType) {
        case 'goals':
          setLoadingGoalsOnly(true);
          break;
        case 'goalsAndObjectives':
          setLoadingGoalsAndObjectives(true);
          break;
        default:
          setLoading(true);
      }

      const currentEmployee = currentUser?.employees;
      let company_id;
      let login_user_id;
      if (currentEmployee && currentEmployee?.length) {
        company_id = currentEmployee[userold?.currentCompany]?.company_id;
        login_user_id = currentEmployee[userold?.currentCompany]?.user_id;
      }

      // Save each goal from the editableGoals
      for (const goal of editableGoals) {
        // First, save the goal
        const goalResult = await api.post("/goal/", {
          title: goal.title,
          description: goal.description,
          company_id: company_id,
          user_id: [login_user_id],
          session: (!goal.session || goal.session === "0") ? getCurrentYearAndQuarter() : goal.session,
        });

        if (goalResult?.status === 201 || goalResult?.status === 200) {
          const goalId = goalResult.data?.goal_id;
          toast.success("Goal \"" + goal.title + "\" saved successfully!");

          // Only proceed with objectives if we're not just saving goals
          if (saveType !== 'goals') {
            // Then save each objective for this goal
            for (const objective of goal.children || []) {
              const objectiveResult = await api.post("/okr/", {
                title: objective.objective_title,
                description: objective.objective_description,
                company_id: company_id,
                user_id: [login_user_id],
                goal_id: goalId,
                parent: true,
                session: (!goal.session || goal.session === "0") ? getCurrentYearAndQuarter() : goal.session,
              });

              if (objectiveResult?.status === 201 || objectiveResult?.status === 200) {
                const objectiveId = objectiveResult.data?.okr_id;
                toast.success("Objective \"" + objective.objective_title + "\" saved successfully!");

                // Only proceed with key results if we're saving everything
                if (saveType === 'all') {
                  // Finally, save each key result for this objective
                  for (const kr of objective.children || []) {
                    const krResult = await api.post("/key_results/", {
                      title: kr.key_result_title,
                      description: kr.key_result_description,
                      unit: kr.unit,
                      key_result_type: kr.key_result_type,
                      initial_number: kr.initial_number,
                      target_number: kr.target_number,
                      deadline: kr.deadline,
                      okr_id: objectiveId,
                      company_id: company_id,
                      user_id: [login_user_id],
                    });

                    if (krResult?.status === 201 || krResult?.status === 200) {
                      toast.success("Key Result \"" + kr.key_result_title + "\" saved successfully!");
                    } else {
                      throw new Error("Failed to save key result");
                    }
                  }
                }
              } else {
                throw new Error("Failed to save objective");
              }
            }
          }
        } else {
          throw new Error("Failed to save goal");
        }
      }
      setIsSaved(true);
    } catch (error) {
      console.error("Error saving data:", error);
      const errorMessage = saveType === 'goals' 
        ? "Failed to save goals" 
        : saveType === 'goalsAndObjectives' 
          ? "Failed to save goals and objectives" 
          : "Failed to save goals";
      toast.error(error?.response?.data?.error || errorMessage);
    } finally {
      // Reset appropriate loading state based on save type
      switch (saveType) {
        case 'goals':
          setLoadingGoalsOnly(false);
          break;
        case 'goalsAndObjectives':
          setLoadingGoalsAndObjectives(false);
          break;
        default:
          setLoading(false);
      }
    }
  };

  return (
    <div>
      {editableGoals.map((goal, goalIndex) => (
        <div key={goalIndex}>
          <div>
            <div className="flex gap-x-2">
              <button
                className="rounded p-1"
                style={{
                  background:
                    "linear-gradient(140.34deg, rgba(252, 8, 0, 0.29) 9.12%, rgba(253, 64, 64, 0) 92.07%)",
                }}
              >
                <GoGoal
                  className="text-md"
                  style={{
                    color: "#B91C1C",
                    width: "18px",
                    height: "18px",
                  }}
                />
              </button>
              <strong className="flex items-center">Goal</strong>
              <button
                onClick={() => handleEdit('goal', goalIndex)}
                className="ml-2 text-gray-600 hover:text-gray-800"
              >
                {goal.isEditing ? <SaveIcon /> : <EditIcon />}
              </button>
            </div>
            {goal.isEditing ? (
              <div className="pr-4">
                <p>
                  <ArrowRightIcon /> Title:{" "}
                  <input
                    type="text"
                    value={goal.title}
                    onChange={(e) => handleInputChange('goal', goalIndex, 'title', e.target.value)}
                    className="border rounded px-2 py-1 w-full"
                  />
                </p>
                <p>
                  <ArrowRightIcon /> Description:{" "}
                  <textarea
                    value={goal.description}
                    onChange={(e) => handleInputChange('goal', goalIndex, 'description', e.target.value)}
                    className="border rounded px-2 py-1 w-full"
                  />
                </p>
                <p>
                  <ArrowRightIcon /> Quarter:{" "}
                  <Select
                    value={goal.session}
                    onChange={(value) => handleInputChange('goal', goalIndex, 'session', value)}
                    className="w-full"
                    placeholder="Select Quarter"
                  >
                    {Sessions?.map((session, index) => (
                      <Select.Option key={index} value={session.value}>
                        {session.label}
                      </Select.Option>
                    ))}
                  </Select>
                </p>
              </div>
            ) : (
              <>
                <p>
                  <ArrowRightIcon /> Title: {goal.title}
                </p>
                <p>
                  <ArrowRightIcon /> Description: {goal.description}
                </p>
                <p>
                  <ArrowRightIcon /> Quarter: {goal.session}
                </p>
              </>
            )}
            {goal.children?.map((objective, objIndex) => (
              <div key={objIndex} className="mt-2 ml-2">
                <div className="flex gap-x-2">
                  <button>
                    <img
                      src="/assets/images/objective.svg"
                      style={{
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </button>
                  <strong className="flex items-center">Objective</strong>
                  <button
                    onClick={() => handleEdit('objective', objIndex, null, goalIndex)}
                    className="ml-2 text-gray-600 hover:text-gray-800"
                  >
                    {objective.isEditing ? <SaveIcon /> : <EditIcon />}
                  </button>
                </div>
                {objective.isEditing ? (
                  <div className="pr-4">
                    <p>
                      <ArrowRightIcon /> Title:{" "}
                      <input
                        type="text"
                        value={objective.objective_title}
                        onChange={(e) => handleInputChange('objective', objIndex, 'objective_title', e.target.value, null, goalIndex)}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </p>
                    <p>
                      <ArrowRightIcon /> Description:{" "}
                      <textarea
                        value={objective.objective_description}
                        onChange={(e) => handleInputChange('objective', objIndex, 'objective_description', e.target.value, null, goalIndex)}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </p>
                  </div>
                ) : (
                  <>
                    <p>
                      <ArrowRightIcon /> Title: {objective.objective_title}
                    </p>
                    <p>
                      <ArrowRightIcon /> Description: {objective.objective_description}
                    </p>
                  </>
                )}
                
                {objective.children?.map((kr, krIndex) => (
                  <div key={krIndex} className="mt-2 ml-4">
                    <p className="flex gap-x-2">
                      <button>
                        <img
                          src="/assets/images/keyresults.svg"
                          style={{
                            width: "20px",
                            height: "20px",
                          }}
                        />
                      </button>
                      <strong className="flex items-center">Key Result</strong>
                      <button
                        onClick={() => handleEdit('keyResult', krIndex, objIndex, goalIndex)}
                        className="ml-2 text-gray-600 hover:text-gray-800"
                      >
                        {kr.isEditing ? <SaveIcon /> : <EditIcon />}
                      </button>
                    </p>
                    {kr.isEditing ? (
                      <div className="pr-4">
                        <p>
                          <ArrowRightIcon /> Title:{" "}
                          <input
                            type="text"
                            value={kr.key_result_title}
                            onChange={(e) => handleInputChange('keyResult', krIndex, 'key_result_title', e.target.value, objIndex, goalIndex)}
                            className="border rounded px-2 py-1 w-full"
                          />
                        </p>
                        <p>
                          <ArrowRightIcon /> Description:{" "}
                          <textarea
                            value={kr.key_result_description}
                            onChange={(e) => handleInputChange('keyResult', krIndex, 'key_result_description', e.target.value, objIndex, goalIndex)}
                            className="border rounded px-2 py-1 w-full"
                          />
                        </p>
                        <p>
                          <ArrowRightIcon /> Unit:{" "}
                          <Select
                            value={kr.unit}
                            onChange={(value) => handleInputChange('keyResult', krIndex, 'unit', value, objIndex, goalIndex)}
                            className="w-full"
                            placeholder="Select Unit"
                          >
                            {Units?.map((unit, index) => (
                              <Select.Option key={index} value={unit.value}>
                                {unit.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </p>
                        <p>
                          <ArrowRightIcon /> Type:{" "}
                          <Select
                            value={kr.key_result_type}
                            onChange={(value) => handleInputChange('keyResult', krIndex, 'key_result_type', value, objIndex, goalIndex)}
                            className="w-full"
                            placeholder="Select Type"
                          >
                            <Select.Option value="Should increase to">Should increase to</Select.Option>
                            <Select.Option value="Should decrease to">Should decrease to</Select.Option>
                          </Select>
                        </p>
                        <p>
                          <ArrowRightIcon /> Initial Number:{" "}
                          <input
                            type="number"
                            value={kr.initial_number}
                            onChange={(e) => handleInputChange('keyResult', krIndex, 'initial_number', e.target.value, objIndex, goalIndex)}
                            className="border rounded px-2 py-1 w-full"
                          />
                        </p>
                        <p>
                          <ArrowRightIcon /> Target Number:{" "}
                          <input
                            type="number"
                            value={kr.target_number}
                            onChange={(e) => handleInputChange('keyResult', krIndex, 'target_number', e.target.value, objIndex, goalIndex)}
                            className="border rounded px-2 py-1 w-full"
                          />
                        </p>
                        <p>
                          <ArrowRightIcon /> Deadline:{" "}
                          <input
                            type="date"
                            value={kr.deadline}
                            onChange={(e) => handleInputChange('keyResult', krIndex, 'deadline', e.target.value, objIndex, goalIndex)}
                            className="border rounded px-2 py-1 w-full"
                          />
                        </p>
                      </div>
                    ) : (
                      <>
                        <p>
                          <ArrowRightIcon /> Title: {kr.key_result_title}
                        </p>
                        <p>
                          <ArrowRightIcon /> Description: {kr.key_result_description}
                        </p>
                        <p>
                          <ArrowRightIcon /> Unit: {kr.unit}
                        </p>
                        <p>
                          <ArrowRightIcon /> Type: {kr.key_result_type}
                        </p>
                        <p>
                          <ArrowRightIcon /> Initial Number: {kr.initial_number}
                        </p>
                        <p>
                          <ArrowRightIcon /> Target Number: {kr.target_number}
                        </p>
                        <p>
                          <ArrowRightIcon /> Deadline: {kr.deadline}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex justify-end mt-4 gap-x-2">
        {!isSaved ? (
          <>
            <button
              onClick={() => saveData('all')}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
              title='You will be able to edit the goals, objectives and key results after saving in the main app'
            >
              {loading ? 'Saving...' : 'Save All'}
            </button>
           
          </>
        ) : (
          <button
            onClick={handleClose}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded"
            title="Saved successfully, you can close now"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default GoalsFromFileResponseMessage; 